import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Building2, Users, FolderKanban, AlertTriangle, TrendingUp, Activity } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }

    try {
      const [statsRes, logsRes] = await Promise.all([
        fetch(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API}/admin/logs?limit=10`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      if (logsRes.ok) {
        setRecentLogs(await logsRes.json());
      }
    } catch (err) {
      console.error("Failed to fetch admin data:", err);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      label: "Organisationer", 
      value: stats?.total_organizations || 0, 
      icon: Building2, 
      color: "bg-blue-500",
      description: `${stats?.active_organizations || 0} aktiva`
    },
    { 
      label: "Användare", 
      value: stats?.total_users || 0, 
      icon: Users, 
      color: "bg-green-500",
      description: `+${stats?.users_last_30_days || 0} senaste 30 dagar`
    },
    { 
      label: "Projekt", 
      value: stats?.total_projects || 0, 
      icon: FolderKanban, 
      color: "bg-purple-500",
      description: "Totalt antal"
    },
    { 
      label: "Avvikelser", 
      value: stats?.total_incidents || 0, 
      icon: AlertTriangle, 
      color: "bg-amber-500",
      description: "Rapporterade"
    },
  ];

  const eventTypeLabels = {
    user_registered: "Ny användare",
    user_login: "Inloggning",
    superadmin_login: "Admin inloggning",
    organization_created: "Ny organisation",
    organization_updated: "Organisation uppdaterad",
    incident_created: "Avvikelse rapporterad",
    user_created: "Användare skapad",
    user_updated: "Användare uppdaterad",
    user_deactivated: "Användare inaktiverad"
  };

  const eventTypeColors = {
    user_registered: "bg-green-500",
    superadmin_login: "bg-amber-500",
    organization_created: "bg-blue-500",
    incident_created: "bg-red-500",
    user_created: "bg-green-500",
    user_deactivated: "bg-red-500"
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
          Dashboard
        </h1>
        <p className="text-slate-400 mt-1">Översikt av systemet</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <Card key={index} className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${stat.color}/20`}>
                  <stat.icon className={`w-6 h-6 ${stat.color.replace('bg-', 'text-')}`} />
                </div>
                <div>
                  <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
                    {stat.value}
                  </p>
                  <p className="text-sm text-slate-400">{stat.label}</p>
                  <p className="text-xs text-slate-500 mt-1">{stat.description}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Activity */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
            <Activity className="w-5 h-5 text-amber-500" />
            Senaste aktivitet
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentLogs.length === 0 ? (
            <p className="text-slate-400 text-center py-8">Ingen aktivitet ännu</p>
          ) : (
            <div className="space-y-3">
              {recentLogs.map((log, index) => (
                <div 
                  key={log.log_id || index} 
                  className="flex items-start gap-3 p-3 bg-slate-700/30 rounded-lg"
                >
                  <div className={`w-2 h-2 rounded-full mt-2 ${eventTypeColors[log.event_type] || 'bg-slate-500'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-amber-500">
                        {eventTypeLabels[log.event_type] || log.event_type}
                      </span>
                      <span className="text-xs text-slate-500">
                        {new Date(log.created_at).toLocaleString('sv-SE')}
                      </span>
                    </div>
                    <p className="text-sm text-slate-300 mt-1">{log.description}</p>
                    {log.user_email && (
                      <p className="text-xs text-slate-500 mt-1">{log.user_email}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
